/* eslint-disable no-unused-vars */
import { useState, useContext, useEffect } from "react";
import { AdminContext } from "../../../context/AdminContext";
import { useNavigate, useParams } from "react-router-dom";
import {
  ArrowLeft,
  Upload,
  X,
  Globe,
  Lock,
  Crown,
  Tag,
  Image as ImageIcon,
  Save,
  Send,
  Languages,
} from "lucide-react";
import { useTheme } from "../../../context/ThemeContext";

const BlogForm = () => {
  const { blogId } = useParams();
  const isEditing = Boolean(blogId);
  const navigate = useNavigate();
  const { isDarkMode } = useTheme();
  const {
    fetchBlogById,
    createBlog,
    updateBlog,
    fetchBlogCategories,
    blogCategories,
  } = useContext(AdminContext);

  const [activeTab, setActiveTab] = useState("en");
  const [loading, setLoading] = useState(false);
  const [fetchLoading, setFetchLoading] = useState(isEditing);
  const [coverPreview, setCoverPreview] = useState(null);
  const [coverFile, setCoverFile] = useState(null);

  const [form, setForm] = useState({
    titleEn: "",
    titleAm: "",
    contentEn: "",
    contentAm: "",
    summaryEn: "",
    summaryAm: "",
    slug: "",
    metaDescription: "",
    category: "",
    tags: "",
    visibility: "public",
    status: "draft",
    isFeatured: false,
    isPinned: false,
    changeNote: "",
  });

  useEffect(() => {
    fetchBlogCategories();
  }, [fetchBlogCategories]);

  useEffect(() => {
    if (isEditing) {
      loadBlog();
    }
  }, [blogId]);

  const loadBlog = async () => {
    setFetchLoading(true);
    const data = await fetchBlogById(blogId);
    if (data?.success && data.blog) {
      const b = data.blog;
      setForm({
        titleEn: b.titleEn || "",
        titleAm: b.titleAm || "",
        contentEn: b.contentEn || "",
        contentAm: b.contentAm || "",
        summaryEn: b.summaryEn || "",
        summaryAm: b.summaryAm || "",
        slug: b.slug || "",
        metaDescription: b.metaDescription || "",
        category: b.categoryId || b.category?.id || b.category || "",
        tags: (b.tags || []).join(", "),
        visibility: b.visibility || "public",
        status: b.status || "draft",
        isFeatured: b.isFeatured || false,
        isPinned: b.isPinned || false,
        changeNote: "",
      });
      if (b.coverImage) {
        setCoverPreview(b.coverImage);
      }
    }
    setFetchLoading(false);
  };

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    setForm((prev) => ({
      ...prev,
      [name]: type === "checkbox" ? checked : value,
    }));
  };

  const handleCoverImage = (e) => {
    const file = e.target.files[0];
    if (file) {
      setCoverFile(file);
      setCoverPreview(URL.createObjectURL(file));
    }
  };

  const removeCoverImage = () => {
    setCoverFile(null);
    setCoverPreview(null);
  };

  const generateSlug = () => {
    const slug = form.titleEn
      .toLowerCase()
      .replace(/[^a-z0-9\s-]/g, "")
      .replace(/\s+/g, "-")
      .replace(/-+/g, "-")
      .trim();
    setForm((prev) => ({ ...prev, slug }));
  };

  const estimateReadingTime = () => {
    const words = Math.max(
      (form.contentEn || "").split(/\s+/).length,
      (form.contentAm || "").split(/\s+/).length,
    );
    return Math.max(1, Math.ceil(words / 200));
  };

  const handleSubmit = async (submitStatus) => {
    if (!form.titleEn.trim()) {
      return alert("English title is required");
    }
    if (!form.contentEn.trim()) {
      return alert("English content is required");
    }
    if (!form.category) {
      return alert("Category is required");
    }

    setLoading(true);
    const formData = new FormData();
    formData.append("titleEn", form.titleEn);
    formData.append("titleAm", form.titleAm);
    formData.append("contentEn", form.contentEn);
    formData.append("contentAm", form.contentAm);
    formData.append("summaryEn", form.summaryEn);
    formData.append("summaryAm", form.summaryAm);
    formData.append("slug", form.slug);
    formData.append("metaDescription", form.metaDescription);
    formData.append("categoryId", form.category);
    formData.append("tags", form.tags);
    formData.append("visibility", form.visibility);
    formData.append("status", submitStatus || form.status);
    formData.append("isFeatured", form.isFeatured);
    formData.append("isPinned", form.isPinned);
    if (form.changeNote) formData.append("changeNote", form.changeNote);
    if (coverFile) formData.append("coverImage", coverFile);

    let result;
    if (isEditing) {
      result = await updateBlog(blogId, formData);
    } else {
      result = await createBlog(formData);
    }

    setLoading(false);
    if (result?.success) {
      navigate("/parent-hub/blogs");
    }
  };

  if (fetchLoading) {
    return (
      <div className="flex items-center justify-center py-20">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary"></div>
      </div>
    );
  }

  return (
    <div className="animate-in fade-in slide-in-from-bottom-4 duration-500 pb-12 max-w-5xl mx-auto">
      {/* Header */}
      <div className="flex items-center gap-4 mb-8">
        <button
          onClick={() => navigate("/parent-hub/blogs")}
          className="p-2 rounded-xl hover:bg-gray-100 dark:hover:bg-gray-800 transition-colors"
        >
          <ArrowLeft size={20} className="text-gray-500" />
        </button>
        <div>
          <h1 className="text-2xl font-bold text-text dark:text-dark-text">
            {isEditing ? "Edit Blog Post" : "Create New Blog Post"}
          </h1>
          <p className="text-gray-500 dark:text-dark-muted text-sm mt-1">
            {isEditing
              ? "Update blog content and settings"
              : "Write and configure a new blog post"}
          </p>
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Main Content */}
        <div className="lg:col-span-2 space-y-6">
          {/* Language Tabs */}
          <div className="bg-card dark:bg-dark-surface rounded-3xl shadow-sm border border-gray-100 dark:border-dark-border p-6">
            <div className="flex items-center gap-2 mb-6">
              <Languages size={18} className="text-primary" />
              <div className="flex gap-2">
                <button
                  onClick={() => setActiveTab("en")}
                  className={`px-4 py-2 rounded-xl text-sm font-bold transition-colors ${
                    activeTab === "en"
                      ? "bg-primary text-white"
                      : "bg-gray-100 dark:bg-gray-800 text-gray-600 dark:text-dark-muted hover:bg-gray-200 dark:hover:bg-gray-700"
                  }`}
                >
                  🇬🇧 English
                </button>
                <button
                  onClick={() => setActiveTab("am")}
                  className={`px-4 py-2 rounded-xl text-sm font-bold transition-colors ${
                    activeTab === "am"
                      ? "bg-primary text-white"
                      : "bg-gray-100 dark:bg-gray-800 text-gray-600 dark:text-dark-muted hover:bg-gray-200 dark:hover:bg-gray-700"
                  }`}
                >
                  🇪🇹 አማርኛ
                </button>
              </div>
            </div>

            {/* Title */}
            <div className="mb-4">
              <label className="block text-sm font-bold text-text dark:text-dark-text mb-2">
                Title{" "}
                {activeTab === "en" && <span className="text-red-500">*</span>}
              </label>
              <input
                type="text"
                name={activeTab === "en" ? "titleEn" : "titleAm"}
                value={activeTab === "en" ? form.titleEn : form.titleAm}
                onChange={handleChange}
                onBlur={() =>
                  activeTab === "en" && !form.slug && generateSlug()
                }
                placeholder={
                  activeTab === "en"
                    ? "Enter blog title..."
                    : "የብሎግ ርዕስ ያስገቡ..."
                }
                className="w-full px-4 py-3 rounded-2xl bg-gray-50 dark:bg-gray-900 border-none outline-none focus:ring-2 focus:ring-primary/20 text-text dark:text-dark-text placeholder-gray-400 font-medium text-lg"
              />
            </div>

            {/* Summary */}
            <div className="mb-4">
              <label className="block text-sm font-bold text-text dark:text-dark-text mb-2">
                Summary
              </label>
              <input
                type="text"
                name={activeTab === "en" ? "summaryEn" : "summaryAm"}
                value={activeTab === "en" ? form.summaryEn : form.summaryAm}
                onChange={handleChange}
                placeholder={
                  activeTab === "en"
                    ? "Brief summary for listing..."
                    : "አጭር ማጠቃለያ..."
                }
                className="w-full px-4 py-3 rounded-2xl bg-gray-50 dark:bg-gray-900 border-none outline-none focus:ring-2 focus:ring-primary/20 text-text dark:text-dark-text placeholder-gray-400 font-medium"
              />
            </div>

            {/* Content */}
            <div>
              <label className="block text-sm font-bold text-text dark:text-dark-text mb-2">
                Content{" "}
                {activeTab === "en" && <span className="text-red-500">*</span>}
              </label>
              <textarea
                name={activeTab === "en" ? "contentEn" : "contentAm"}
                value={activeTab === "en" ? form.contentEn : form.contentAm}
                onChange={handleChange}
                placeholder={
                  activeTab === "en"
                    ? "Write your blog content here... (HTML supported)"
                    : "የብሎግ ይዘት ያስገቡ..."
                }
                rows={16}
                className="w-full px-4 py-3 rounded-2xl bg-gray-50 dark:bg-gray-900 border-none outline-none focus:ring-2 focus:ring-primary/20 text-text dark:text-dark-text placeholder-gray-400 font-medium resize-y"
              />
            </div>
          </div>

          {/* SEO */}
          <div className="bg-card dark:bg-dark-surface rounded-3xl shadow-sm border border-gray-100 dark:border-dark-border p-6">
            <h3 className="font-bold text-text dark:text-dark-text mb-4">
              SEO Settings
            </h3>
            <div className="space-y-4">
              <div>
                <label className="block text-sm font-bold text-text dark:text-dark-text mb-2">
                  Slug
                </label>
                <div className="flex gap-2">
                  <input
                    type="text"
                    name="slug"
                    value={form.slug}
                    onChange={handleChange}
                    placeholder="auto-generated-from-title"
                    className="flex-1 px-4 py-3 rounded-2xl bg-gray-50 dark:bg-gray-900 border-none outline-none focus:ring-2 focus:ring-primary/20 text-text dark:text-dark-text placeholder-gray-400 font-medium"
                  />
                  <button
                    onClick={generateSlug}
                    type="button"
                    className="px-4 py-3 bg-gray-100 dark:bg-gray-800 rounded-2xl text-sm font-bold text-gray-600 dark:text-dark-muted hover:bg-gray-200 dark:hover:bg-gray-700 transition-colors"
                  >
                    Generate
                  </button>
                </div>
              </div>
              <div>
                <label className="block text-sm font-bold text-text dark:text-dark-text mb-2">
                  Meta Description ({form.metaDescription.length}/160)
                </label>
                <input
                  type="text"
                  name="metaDescription"
                  value={form.metaDescription}
                  onChange={handleChange}
                  maxLength={160}
                  placeholder="SEO meta description..."
                  className="w-full px-4 py-3 rounded-2xl bg-gray-50 dark:bg-gray-900 border-none outline-none focus:ring-2 focus:ring-primary/20 text-text dark:text-dark-text placeholder-gray-400 font-medium"
                />
              </div>
            </div>
          </div>

          {/* Change Note (edit only) */}
          {isEditing && (
            <div className="bg-card dark:bg-dark-surface rounded-3xl shadow-sm border border-gray-100 dark:border-dark-border p-6">
              <h3 className="font-bold text-text dark:text-dark-text mb-4">
                Version Note
              </h3>
              <input
                type="text"
                name="changeNote"
                value={form.changeNote}
                onChange={handleChange}
                placeholder="What did you change? (optional)"
                className="w-full px-4 py-3 rounded-2xl bg-gray-50 dark:bg-gray-900 border-none outline-none focus:ring-2 focus:ring-primary/20 text-text dark:text-dark-text placeholder-gray-400 font-medium"
              />
            </div>
          )}
        </div>

        {/* Sidebar */}
        <div className="space-y-6">
          {/* Cover Image */}
          <div className="bg-card dark:bg-dark-surface rounded-3xl shadow-sm border border-gray-100 dark:border-dark-border p-6">
            <h3 className="font-bold text-text dark:text-dark-text mb-4 flex items-center gap-2">
              <ImageIcon size={16} /> Cover Image
            </h3>
            {coverPreview ? (
              <div className="relative">
                <img
                  src={coverPreview}
                  alt="Cover"
                  className="w-full h-40 object-cover rounded-2xl"
                />
                <button
                  onClick={removeCoverImage}
                  className="absolute top-2 right-2 p-1.5 bg-red-500 text-white rounded-full hover:bg-red-600 transition-colors"
                >
                  <X size={14} />
                </button>
              </div>
            ) : (
              <label className="flex flex-col items-center justify-center w-full h-40 bg-gray-50 dark:bg-gray-900 rounded-2xl border-2 border-dashed border-gray-200 dark:border-gray-700 cursor-pointer hover:border-primary/50 transition-colors">
                <Upload size={24} className="text-gray-400 mb-2" />
                <span className="text-sm text-gray-500 font-medium">
                  Click to upload
                </span>
                <span className="text-xs text-gray-400 mt-1">
                  JPG, PNG, WebP — Max 5MB
                </span>
                <input
                  type="file"
                  accept="image/jpeg,image/png,image/webp"
                  onChange={handleCoverImage}
                  className="hidden"
                />
              </label>
            )}
          </div>

          {/* Category */}
          <div className="bg-card dark:bg-dark-surface rounded-3xl shadow-sm border border-gray-100 dark:border-dark-border p-6">
            <h3 className="font-bold text-text dark:text-dark-text mb-4">
              Category <span className="text-red-500">*</span>
            </h3>
            <select
              name="category"
              value={form.category}
              onChange={handleChange}
              className="w-full px-4 py-3 rounded-2xl bg-gray-50 dark:bg-gray-900 border-none outline-none focus:ring-2 focus:ring-primary/20 text-text dark:text-dark-text font-medium appearance-none cursor-pointer"
            >
              <option value="">Select category...</option>
              {blogCategories.map((cat) => (
                <option key={cat.id} value={cat.id}>
                  {cat.icon} {cat.nameEn || cat.name}
                </option>
              ))}
            </select>
          </div>

          {/* Tags */}
          <div className="bg-card dark:bg-dark-surface rounded-3xl shadow-sm border border-gray-100 dark:border-dark-border p-6">
            <h3 className="font-bold text-text dark:text-dark-text mb-4 flex items-center gap-2">
              <Tag size={16} /> Tags
            </h3>
            <input
              type="text"
              name="tags"
              value={form.tags}
              onChange={handleChange}
              placeholder="parenting, health, education"
              className="w-full px-4 py-3 rounded-2xl bg-gray-50 dark:bg-gray-900 border-none outline-none focus:ring-2 focus:ring-primary/20 text-text dark:text-dark-text placeholder-gray-400 font-medium"
            />
            <p className="text-xs text-gray-400 mt-2">Comma-separated</p>
            {form.tags && (
              <div className="flex flex-wrap gap-2 mt-3">
                {form.tags
                  .split(",")
                  .map((t) => t.trim())
                  .filter(Boolean)
                  .map((tag) => (
                    <span
                      key={tag}
                      className="px-3 py-1 bg-primary/10 text-primary text-xs font-bold rounded-full"
                    >
                      {tag}
                    </span>
                  ))}
              </div>
            )}
          </div>

          {/* Visibility */}
          <div className="bg-card dark:bg-dark-surface rounded-3xl shadow-sm border border-gray-100 dark:border-dark-border p-6">
            <h3 className="font-bold text-text dark:text-dark-text mb-4">
              Visibility
            </h3>
            <div className="space-y-2">
              {[
                { val: "public", icon: Globe, label: "Public" },
                { val: "private", icon: Lock, label: "Private" },
                { val: "premium", icon: Crown, label: "Premium" },
              ].map(({ val, icon: Icon, label }) => (
                <label
                  key={val}
                  className={`flex items-center gap-3 p-3 rounded-xl cursor-pointer transition-colors ${
                    form.visibility === val
                      ? "bg-primary/10 border border-primary/30"
                      : "hover:bg-gray-50 dark:hover:bg-gray-800"
                  }`}
                >
                  <input
                    type="radio"
                    name="visibility"
                    value={val}
                    checked={form.visibility === val}
                    onChange={handleChange}
                    className="text-primary focus:ring-primary"
                  />
                  <Icon size={16} className="text-gray-500" />
                  <span className="text-sm font-medium text-text dark:text-dark-text">
                    {label}
                  </span>
                </label>
              ))}
            </div>
          </div>


          {/* Toggles */}
          <div className="bg-card dark:bg-dark-surface rounded-3xl shadow-sm border border-gray-100 dark:border-dark-border p-6 space-y-3">
            <label className="flex items-center justify-between cursor-pointer">
              <span className="text-sm font-medium text-text dark:text-dark-text">
                ⭐ Featured
              </span>
              <input
                type="checkbox"
                name="isFeatured"
                checked={form.isFeatured}
                onChange={handleChange}
                className="rounded text-primary focus:ring-primary h-5 w-5"
              />
            </label>
            <label className="flex items-center justify-between cursor-pointer">
              <span className="text-sm font-medium text-text dark:text-dark-text">
                📌 Pinned
              </span>
              <input
                type="checkbox"
                name="isPinned"
                checked={form.isPinned}
                onChange={handleChange}
                className="rounded text-primary focus:ring-primary h-5 w-5"
              />
            </label>
          </div>

          {/* Info */}
          <div className="bg-accent dark:bg-gray-800/50 rounded-2xl p-4 text-center">
            <p className="text-sm text-gray-500 dark:text-dark-muted">
              ⏱️ Est. reading time: <strong>{estimateReadingTime()} min</strong>
            </p>
          </div>

          {/* Submit Buttons */}
          <div className="space-y-3">
            <button
              onClick={() => handleSubmit("draft")}
              disabled={loading}
              className="w-full flex items-center justify-center gap-2 px-5 py-3 bg-gray-100 dark:bg-gray-800 hover:bg-gray-200 dark:hover:bg-gray-700 text-text dark:text-dark-text rounded-2xl text-sm font-bold transition-colors disabled:opacity-50"
            >
              <Save size={16} /> Save as Draft
            </button>
            <button
              onClick={() => handleSubmit("published")}
              disabled={loading}
              className="w-full flex items-center justify-center gap-2 px-5 py-3 bg-primary hover:bg-primary/90 text-white rounded-2xl text-sm font-bold transition-colors shadow-lg shadow-primary/20 disabled:opacity-50"
            >
              <Send size={16} /> Publish Now
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default BlogForm;
